import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty, getQueryParamsAsObject } from '@app/utils';
import { STATUS } from '@app/constants/commonConstants';
import { setCanonicalUrlOnScroll } from './utils';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { PAGE_SIZE } from '../constants';
import EmptyProductResults from './EmptyProductResults/EmptyProductResults';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import EquipmentNoResults from '@app/components/common/MyEquipmentFilter/EquipmentInformation/EquipmentNoResults/EquipmentNoResults';
import {
  getPreviousPlpDetails,
  setPLPProductCardExpanded
} from '@app/store/plp/actions';
import ProductComparisonGrid from '@app/components/common/ProductComparisonGrid/ProductComparisonGrid';
import Espot from '@app/components/common/Espot/Espot';
import queryString from 'query-string';
import ProductListLoadPrevious from '@app/components/common/ProductListLoadPrevious';

import { PLP_CATEGORY_GA_LIST_NAME } from '@app/components/common/analytics/analyticsConstants';
import ProductListBottomContainer from './ProductListBottomContainer/ProductListBottomContainer';
import BackToTopButton from '@app/components/common/BackToTopButton';
import Conditional from '@app/components/common/Conditional';

const ProductListComparisonGrid = ({
  getFitmentResult,
  getFitmentCheck,
  onCheck,
  selectedEquipment,
  espotData
}) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();
  const queryParams = getQueryParamsAsObject();
  const { pageSize = PAGE_SIZE, productBeginIndex = 0 } = queryParams;
  const lowestLoadedPage = useSelector(state => state.plpUI.lowestLoadedPage);
  const currentCategory = useSelector(s => s.plpUI.current);
  const catalogEntries = useSelector(
    state => state.categories.byId[currentCategory]?.productsId
  );
  const isCatalogEntriesEmpty = isEmpty(catalogEntries);

  const products = useSelector(state => state.products.byId);
  const productsArrayFilter = useMemo(() => {
    return !isEmpty(products) && !isEmpty(catalogEntries)
      ? catalogEntries.map(partNumber => products[partNumber])
      : [];
  }, [products, catalogEntries]);

  const [productBeginIndexMin, setProductBeginIndexMin] =
    useState(productBeginIndex);

  const getPrevPlpDetailsStatus = useSelector(
    state => state.plpUI.getPrevPlpDetailsStatus
  );
  const recordSetTotal = useSelector(
    state => state.categories.byId[currentCategory]?.productCount
  );
  const getPlpDetailsStatus = useSelector(s => s.plpUI.getPlpDetailsStatus);

  const priceAndAvailibiltyStatus = useSelector(
    state => state.plpUI.priceAndAvailibiltyStatus
  );
  const partsWithFitmentData = useSelector(
    state => state.plpUI.partsWithFitmentData
  );
  const equipmentFitmentStatus = useSelector(
    state => state.plpUI.equipmentFitmentStatus
  );
  const priceData = useSelector(state => state.products.priceAndAvailability);
  const serialNumber = useSelector(
    s => s.myEquipment.equipments.selectedEquipment.serialNumber
  );
  const imageUrl = useSelector(
    s => s.myEquipment.equipments.selectedEquipment.imageUrl
  );
  const model = useSelector(
    s => s.myEquipment.equipments.selectedEquipment.model
  );
  const equipmentFamily = useSelector(
    s => s.myEquipment.equipments.selectedEquipment.equipmentFamily
  );

  const isFilterByEquipmentEnabled = useSelector(
    s => s.categories.byId[currentCategory]?.isFilterByEquipmentEnabled
  );
  const filterByEquipment = useSelector(s => s.plpUI.filterByEquipment);
  const unitOfMeasure = useSelector(s => s.plpUI.unitOfMeasure);
  const facets = useSelector(
    s => s.categories.byId[currentCategory]?.facets || []
  );
  const productCardExpandedArr = useSelector(
    s => s.plpUI.productCardExpandedArr ?? []
  );
  const didFitmentCallResolve =
    equipmentFitmentStatus === STATUS.RESOLVED ||
    equipmentFitmentStatus === STATUS.REJECTED;
  const shouldShowFitmentData = !!(
    serialNumber &&
    isFilterByEquipmentEnabled &&
    filterByEquipment &&
    getPlpDetailsStatus !== STATUS.PENDING
  );

  // Getting if data is being loaded to pass on to second call of ProductListItemsCount (bottom of page)
  const getMorePlpDetailsStatus = useSelector(
    state => state.plpUI.getMorePlpDetailsStatus
  );
  const isLoadingGetMoreResults = getMorePlpDetailsStatus === STATUS.PENDING;
  const showLoadMore =
    recordSetTotal > PAGE_SIZE &&
    recordSetTotal >
      (catalogEntries || []).length + (lowestLoadedPage - 1) * PAGE_SIZE;
  const showBackToTopRef = useRef(false);
  const minimunPageDifference =
    +productBeginIndex / PAGE_SIZE - +productBeginIndexMin / PAGE_SIZE;
  if (productsArrayFilter.length > 32) {
    if (minimunPageDifference >= 2) {
      showBackToTopRef.current = true;
    }
    if (minimunPageDifference === 0) {
      showBackToTopRef.current = false;
    }
  }

  useEffect(() => {
    if (productBeginIndex < productBeginIndexMin) {
      setProductBeginIndexMin(productBeginIndex);
    }
  }, [productBeginIndex, productBeginIndexMin]);
  const onClickBackToTop = () => {
    const updatedParams = {
      ...queryParams,
      offset: +productBeginIndexMin / PAGE_SIZE,
      beginIndex: +productBeginIndexMin,
      productBeginIndex: +productBeginIndexMin
    };
    history.push(
      `${url}?${queryString.stringify({ ...updatedParams, encode: false })}`
    );
  };

  const handleSetExpanded = uniqueId => {
    dispatch(setPLPProductCardExpanded(uniqueId));
  };
  useEffect(() => {
    const handleScroll = () => {
      setCanonicalUrlOnScroll(history, url);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [history, url]);

  const eSpot = (
    <Espot
      html={espotData}
      className={isCatalogEntriesEmpty ? 'mb-4 mb-md-5' : null}
    />
  );
  const shouldRenderEspot = !isEmpty(espotData);
  const renderAdditionalRowContent = ({ rowIndex, matrixLength }) => {
    const isEspotRow =
      rowIndex === 1 || (matrixLength < 2 && rowIndex === matrixLength - 1);

    if (shouldRenderEspot && isEspotRow) {
      return eSpot;
    } else {
      return null;
    }
  };
  if (!isCatalogEntriesEmpty) {
    return (
      <section className="mb-4 mb-md-5 d-flex flex-column">
        {lowestLoadedPage > 1 && (
          <ProductListLoadPrevious
            lowestLoadedPage={lowestLoadedPage}
            getPreviousDetailsStatus={getPrevPlpDetailsStatus}
            getPrevDetails={getPreviousPlpDetails}
          />
        )}
        <ProductComparisonGrid
          productsArray={productsArrayFilter}
          renderAdditionalRowContent={renderAdditionalRowContent}
          pageSize={pageSize}
          lowestLoadedPage={lowestLoadedPage}
          currentCategory={currentCategory}
          getFitmentCheck={getFitmentCheck}
          getFitmentResult={getFitmentResult}
          priceData={priceData}
          shouldShowFitmentData={shouldShowFitmentData}
          didFitmentCallResolve={didFitmentCallResolve}
          partsWithFitmentData={partsWithFitmentData}
          isCanonicalUrl={true}
          priceAndAvailibiltyStatus={priceAndAvailibiltyStatus}
          domain={ERROR_DOMAIN.PLP}
          path={ERROR_PATH.MAIN}
          handleSetExpanded={handleSetExpanded}
          productCardExpandedArr={productCardExpandedArr}
          showLeftNav={true}
          gaListName={PLP_CATEGORY_GA_LIST_NAME}
          unitOfMeasure={unitOfMeasure}
          facets={facets}
        />

        <ProductListBottomContainer
          isLoadingGetMoreResults={isLoadingGetMoreResults}
          catalogEntriesLength={catalogEntries.length}
          recordSetTotal={recordSetTotal}
          showLoadMore={showLoadMore}
        />
        <Conditional test={showBackToTopRef.current}>
          <BackToTopButton onClick={onClickBackToTop} />
        </Conditional>
      </section>
    );
  } else if (
    selectedEquipment &&
    filterByEquipment &&
    getPlpDetailsStatus !== STATUS.PENDING
  ) {
    return (
      <>
        <EquipmentNoResults
          onCheck={onCheck}
          imageUrl={imageUrl}
          model={model}
          serialNumber={serialNumber}
          equipmentFamily={equipmentFamily}
        />
        {shouldRenderEspot && eSpot}
      </>
    );
  } else if (
    (!filterByEquipment || !selectedEquipment) &&
    getPlpDetailsStatus !== STATUS.PENDING
  ) {
    return (
      <>
        <EmptyProductResults />
        {shouldRenderEspot && eSpot}
      </>
    );
  } else {
    return null;
  }
};

ProductListComparisonGrid.propTypes = {
  selectedEquipment: PropTypes.bool,
  getFitmentResult: PropTypes.func,
  getFitmentCheck: PropTypes.func,
  onCheck: PropTypes.func,
  espotData: PropTypes.arrayOf(PropTypes.string)
};

export default ProductListComparisonGrid;
